import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import APICall, { HttpContentWithData } from 'store/APIClient';
import queryString from 'query-string';

import CATEGORIES from 'routes/Explore/components/Sentence/components/functions_categories.json';
import { useGetDataInTreeFormat } from 'routes/Explore/components/Sentence/hooks/treeData';
import { TreeData } from 'routes/Explore/components/Sentence/helpers/treeData';
import { filterOnlyVisibleElement } from 'routes/Explore/helpers';
import { useSelector } from 'store/hooks';
import { isCurrentlyOnPublicDashboard } from 'helpers/dashboardHelpers';

export const getPublicToken = (): string | undefined => {
    const parsedQuery = queryString.parse(location.search);
    if (parsedQuery.public_token === undefined) return undefined;
    return parsedQuery.public_token as string;
};

const useGetTreeData = (
    filters: SentenceFilterT[],
    dashboardKey: number,
): { treeData: TreeData; isLoading: boolean } => {
    const isSupportUser = useSelector((state) => state.vision.isSupportUser);
    const isOnPublicDashboard = isCurrentlyOnPublicDashboard();
    const publicToken = getPublicToken();

    const getDashboardFilters = async () => {
        const response = await APICall<
            HttpContentWithData<AvailableElementT[]>
        >({
            endpoint: `graph/filters?dashboard=${dashboardKey}`,
            method: 'GET',
            throwWhenNotOk: publicToken === undefined,
        });
        return response.content.data;
    };

    const { data: allFilters, isLoading } = useQuery({
        queryKey: [`get_filters_dashboard_${dashboardKey}`],
        // TODO / DEBT: instead of constructing a string, we should give an array with the prefix and dashboardKey.
        queryFn: getDashboardFilters,
        enabled: dashboardKey >= 0 && !isOnPublicDashboard,
        meta: {
            useGlobalOnError: true,
        },
    });

    const selectedFields = useMemo(
        () => filters.map((f) => f.field),
        [filters],
    );

    const availableMetrics = useMemo(() => {
        if (!allFilters) return [];
        const availableFilters = allFilters.filter(
            (f) => !selectedFields.includes(f.field),
        );
        return filterOnlyVisibleElement(isSupportUser, availableFilters);
    }, [allFilters, selectedFields]);

    const metricsTreeData = useGetDataInTreeFormat(
        availableMetrics,
        CATEGORIES,
    );

    return { treeData: metricsTreeData, isLoading };
};

export default useGetTreeData;
